










































































/**
 * Collections Component
 * Displays Levno collections matching with Fonterra collections.
 * Shows pickups with missing data on either side. Sorted by timestamp.
 */

import { Component, Vue, Prop } from 'vue-property-decorator';

import { ApiUtils } from '@/common/ApiUtils';
import { DefaultApi, Product, ProductsApi } from '@/api';
import {
	matchVats,
	sortPickups,
	matchPickups,
	calculateDeltas,
	sortJointPickups,
	filterFalsePickups,
	formatLevnoPickups,
	formatFonterraPickups,
} from './collections-utils';
import { Pickup, JointPickup } from './collections-types';

import Toast from '@/components/display/toast.vue';

@Component({})
export default class Collections extends Vue {
	@Prop({ type: String, required: true })
	public supplierNumber!: string;
	@Prop({ type: Object, required: true })
	public product!: Product;

	private toast: Toast = new Toast();
	public readonly nDays: number = 60;
	public loading: boolean = false;
	public jointPickups: JointPickup[] = [];
	public levnoPickups: Pickup[] = [];
	public fonterraError = false;

	get useFonterra() {
		return this.product.supplier === 'F' && this.fonterraError === false;
	}

	public collectionFields = [
		{ key: 'levno.start', label: 'Date' },
		{ key: 'levno.volume', label: 'Litres' },
		{ key: 'fonterra.start', label: 'Date' },
		{ key: 'fonterra.volume', label: 'Litres' },
		{ key: 'delta.volume', label: 'Litres' },
		{ key: 'delta.offset', label: 'mm' },
		{ key: 'levno.temperature', label: '', class: 'border-left' },
		{ key: 'fonterra.temperature', label: 'Temperature' },
		{ key: 'delta.temperature', label: '' },
		{ key: 'levno.milkingInProgress', label: '', class: 'border-left text-center' },
		{ key: 'levno.type', label: '', class: 'text-center' },
	];

	public nonFonterraCollectionFields = [
		{ key: 'start', label: 'Date' },
		{ key: 'volume', label: 'Volume (L)' },
		{ key: 'temperature', label: 'Temp (°C)', class: 'border-left' },
		{ key: 'milkingInProgress', label: 'CDM', class: 'border-left text-center' },
		{ key: 'type', label: 'Partial', class: 'text-center' },
	];

	public async created() {
		this.loading = true;
		const levnoPickups: Pickup[] | undefined = sortPickups(
			formatLevnoPickups(await this.getLevnoPickups()),
		);
		// Handle if fonterra is supplier
		if (this.useFonterra) {
			const fonterraPickups: Pickup[] | undefined = sortPickups(
				formatFonterraPickups(await this.getFonterraPickups()),
			);
			// Handle if fonterra is supplier but no fonterra pickups are found
			if (fonterraPickups && fonterraPickups.length === 0) {
				this.fonterraError = true;
				this.levnoPickups = matchVats(filterFalsePickups(levnoPickups), this.product, true) ?? [];
				this.loading = false;
				return;
			}
			this.jointPickups = matchPickups(levnoPickups, fonterraPickups, this.product);
			this.jointPickups = calculateDeltas(this.jointPickups, this.product) ?? [];
			this.jointPickups = sortJointPickups(this.jointPickups) ?? [];
		} else {
			// Handle other suppliers
			this.levnoPickups = matchVats(filterFalsePickups(levnoPickups), this.product, true) ?? [];
		}
		this.loading = false;
	}

	public async getLevnoPickups() {
		if (typeof this.product.id === 'undefined') {
			this.toast.makeToast(
				'Levno pickup error',
				`Failed to get Levno pickups, Id is incorrect`,
				'danger',
			);
			return;
		}

		try {
			const api = new ProductsApi();
			const response = await api.getProductEvents(this.product.id, {
				params: {
					nDays: this.nDays,
				},
			});
			return response;
		} catch (e) {
			this.handleApiError('Levno', e);
			return;
		}
	}

	public async getFonterraPickups() {
		try {
			const api = new DefaultApi();
			const response = await api.getFonterraPickup(this.supplierNumber, {
				params: {
					nDays: this.nDays,
				},
			});
			return response;
		} catch (e) {
			this.handleApiError('Fonterra', e);
			return;
		}
	}

	public handleApiError(pickupType: string, e: unknown) {
		const { errorMessage } = ApiUtils.handleApiError(e, `${pickupType} Pickups`);
		this.toast.makeToast(errorMessage, `Failed to get ${pickupType} pickups`, 'danger');
	}
}
